import { useState, useCallback, useRef, useEffect } from 'react';
import {
  Typography,
  makeStyles,
  IconButton,
  Chip,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Divider,
  Button,
  Collapse,
  Link,
  Box,
  Tooltip,
  Paper,
  CircularProgress,
} from '@material-ui/core';
import CloseIcon from '@material-ui/icons/Close';
import ErrorIcon from '@material-ui/icons/Error';
import WarningIcon from '@material-ui/icons/Warning';
import InfoIcon from '@material-ui/icons/Info';
import CodeIcon from '@material-ui/icons/Code';
import LinkIcon from '@material-ui/icons/Link';
import FileCopyIcon from '@material-ui/icons/FileCopy';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import ExpandLessIcon from '@material-ui/icons/ExpandLess';
import CheckCircleIcon from '@material-ui/icons/CheckCircle';
import HelpOutlineIcon from '@material-ui/icons/HelpOutline';
import { useApi } from '@backstage/core-plugin-api';
import { yamlEditorApiRef, type ValidationError, type FieldInfo } from '../../api';

export const SIDEBAR_DEFAULT_WIDTH = 360;
export const SIDEBAR_MIN_WIDTH = 280;
export const SIDEBAR_MAX_WIDTH = 600;

const useStyles = makeStyles(theme => ({
  sidebarContainer: {
    position: 'relative',
    flexShrink: 0,
    height: '100%',
    display: 'flex',
  },
  resizeHandle: {
    position: 'absolute',
    left: 0,
    top: 0,
    bottom: 0,
    width: 6,
    cursor: 'ew-resize',
    zIndex: 10,
    backgroundColor: 'transparent',
    transition: 'background-color 0.15s ease',
    '&:hover': {
      backgroundColor: theme.palette.primary.main,
    },
  },
  resizeHandleActive: {
    backgroundColor: theme.palette.primary.main,
  },
  sidebar: {
    width: '100%',
    flexShrink: 0,
    height: '100%',
    display: 'flex',
    flexDirection: 'column',
    borderLeft: `1px solid ${theme.palette.divider}`,
    backgroundColor: theme.palette.background.paper,
    overflow: 'hidden',
  },
  header: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: theme.spacing(1.5, 2),
    borderBottom: `1px solid ${theme.palette.divider}`,
    backgroundColor: theme.palette.background.default,
    flexShrink: 0,
  },
  headerTitle: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
  },
  content: {
    padding: theme.spacing(2),
    overflowY: 'auto',
    flex: 1,
  },
  errorSection: {
    marginBottom: theme.spacing(2),
  },
  sectionTitle: {
    fontWeight: 600,
    marginBottom: theme.spacing(1),
    color: theme.palette.text.secondary,
    fontSize: '0.7rem',
    textTransform: 'uppercase',
    letterSpacing: '0.1em',
  },
  errorMessage: {
    fontWeight: 500,
    marginBottom: theme.spacing(0.5),
    fontSize: '0.875rem',
  },
  errorDetails: {
    color: theme.palette.text.secondary,
    fontSize: '0.8rem',
    lineHeight: 1.4,
    marginBottom: theme.spacing(1.5),
  },
  codeBlock: {
    backgroundColor: theme.palette.type === 'dark' 
      ? 'rgba(255, 255, 255, 0.05)' 
      : 'rgba(0, 0, 0, 0.04)',
    padding: theme.spacing(1),
    borderRadius: theme.shape.borderRadius,
    fontFamily: 'monospace',
    fontSize: '0.8rem',
    overflowX: 'auto',
    marginBottom: theme.spacing(1),
  },
  pathChip: {
    fontFamily: 'monospace',
    fontSize: '0.7rem',
    height: 24,
  },
  currentValueLabel: {
    color: theme.palette.error.main,
    fontWeight: 500,
    marginBottom: theme.spacing(0.5),
    fontSize: '0.7rem',
  },
  suggestionsList: {
    padding: 0,
    margin: 0,
  },
  suggestionItem: {
    padding: theme.spacing(0.5, 1),
    borderRadius: theme.shape.borderRadius,
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    '&:hover': {
      backgroundColor: theme.palette.action.hover,
    },
  },
  suggestionText: {
    fontFamily: 'monospace',
    fontSize: '0.8rem',
    flex: 1,
  },
  copyButton: {
    padding: theme.spacing(0.5),
    opacity: 0,
    transition: 'opacity 0.2s',
    '$suggestionItem:hover &': {
      opacity: 1,
    },
  },
  docsLink: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(0.5),
    marginTop: theme.spacing(1.5),
    fontSize: '0.8rem',
  },
  severityChip: {
    marginLeft: theme.spacing(0.5),
    height: 20,
    fontSize: '0.7rem',
  },
  errorChip: {
    backgroundColor: theme.palette.error.light,
    color: theme.palette.error.contrastText,
  },
  warningChip: {
    backgroundColor: theme.palette.warning.light,
    color: theme.palette.warning.contrastText,
  },
  infoChip: {
    backgroundColor: theme.palette.info.light,
    color: theme.palette.info.contrastText,
  },
  errorsList: {
    maxHeight: 180,
    overflowY: 'auto',
    marginBottom: theme.spacing(1.5),
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: theme.shape.borderRadius,
  },
  errorsListItem: {
    cursor: 'pointer',
    padding: theme.spacing(0.75, 1),
    '&:hover': {
      backgroundColor: theme.palette.action.hover,
    },
  },
  selectedError: {
    backgroundColor: theme.palette.action.selected,
    '&:hover': {
      backgroundColor: theme.palette.action.selected,
    },
  },
  noErrors: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    padding: theme.spacing(3),
    color: theme.palette.success.main,
    textAlign: 'center',
  },
  noErrorsIcon: {
    fontSize: 48,
    marginBottom: theme.spacing(1),
    color: theme.palette.success.light,
  },
  locationInfo: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
    marginTop: theme.spacing(0.5),
    color: theme.palette.text.secondary,
    fontSize: '0.7rem',
  },
  expandButton: {
    padding: theme.spacing(0.5),
  },
}));

export interface ErrorDetailsSidebarProps {
  open: boolean;
  onClose: () => void;
  errors: ValidationError[];
  selectedError: ValidationError | null;
  onSelectError: (error: ValidationError | null) => void;
  onGoToLine?: (line: number, column?: number) => void;
  /** Current entity kind for fetching schema info */
  entityKind?: string;
}

export const ErrorDetailsSidebar = ({
  open,
  onClose,
  errors,
  selectedError,
  onSelectError,
  onGoToLine,
  entityKind,
}: ErrorDetailsSidebarProps) => {
  const classes = useStyles();
  const yamlEditorApi = useApi(yamlEditorApiRef);
  const [suggestionsExpanded, setSuggestionsExpanded] = useState(true);
  const [schemaInfoExpanded, setSchemaInfoExpanded] = useState(true);
  const [sidebarWidth, setSidebarWidth] = useState(SIDEBAR_DEFAULT_WIDTH);
  const [isResizing, setIsResizing] = useState(false);
  const resizeStartX = useRef(0);
  const resizeStartWidth = useRef(SIDEBAR_DEFAULT_WIDTH);
  
  // Schema info for the selected error's field
  const [fieldInfo, setFieldInfo] = useState<FieldInfo | null>(null);
  const [loadingFieldInfo, setLoadingFieldInfo] = useState(false);

  // Handle resize start
  const handleResizeStart = useCallback((e: React.MouseEvent) => {
    e.preventDefault();
    setIsResizing(true);
    resizeStartX.current = e.clientX;
    resizeStartWidth.current = sidebarWidth;
  }, [sidebarWidth]);

  // Handle resize move and end
  useEffect(() => {
    if (!isResizing) return undefined;

    const handleMouseMove = (e: MouseEvent) => {
      const deltaX = resizeStartX.current - e.clientX; // Negative direction (dragging left increases width)
      const newWidth = Math.min(
        SIDEBAR_MAX_WIDTH,
        Math.max(SIDEBAR_MIN_WIDTH, resizeStartWidth.current + deltaX)
      );
      setSidebarWidth(newWidth);
    };

    const handleMouseUp = () => {
      setIsResizing(false);
    };

    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);

    // Prevent text selection while resizing
    document.body.style.userSelect = 'none';
    document.body.style.cursor = 'ew-resize';

    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
      document.body.style.userSelect = '';
      document.body.style.cursor = '';
    };
  }, [isResizing]);

  // Fetch field info when selected error changes
  useEffect(() => {
    if (!selectedError?.path || !entityKind) {
      setFieldInfo(null);
      return;
    }

    setLoadingFieldInfo(true);
    yamlEditorApi.getFieldInfo(entityKind, selectedError.path)
      .then(setFieldInfo)
      .catch(() => setFieldInfo(null))
      .finally(() => setLoadingFieldInfo(false));
  }, [selectedError?.path, entityKind, yamlEditorApi]);

  const getSeverityIcon = (severity?: string) => {
    switch (severity) {
      case 'error':
        return <ErrorIcon color="error" fontSize="small" />;
      case 'warning':
        return <WarningIcon style={{ color: '#ff9800' }} fontSize="small" />;
      case 'info':
        return <InfoIcon color="primary" fontSize="small" />;
      default:
        return <ErrorIcon color="error" fontSize="small" />;
    }
  };

  const handleCopySuggestion = useCallback((text: string) => {
    navigator.clipboard.writeText(text);
  }, []);

  const handleGoToError = useCallback(() => {
    if (selectedError?.line && onGoToLine) {
      onGoToLine(selectedError.line, selectedError.column);
    }
  }, [selectedError, onGoToLine]);

  const errorCount = errors.filter(e => e.severity === 'error' || !e.severity).length;
  const warningCount = errors.filter(e => e.severity === 'warning').length;

  if (!open) {
    return null;
  }

  return (
    <div className={classes.sidebarContainer} style={{ width: sidebarWidth }}>
      {/* Resize Handle */}
      {/* eslint-disable-next-line jsx-a11y/no-static-element-interactions, jsx-a11y/no-noninteractive-element-interactions */}
      <div
        className={`${classes.resizeHandle} ${isResizing ? classes.resizeHandleActive : ''}`}
        onMouseDown={handleResizeStart}
        role="separator"
        aria-orientation="vertical"
        aria-label="Resize sidebar"
      />
      <Paper className={classes.sidebar} elevation={0} square>
        {/* Header */}
        <div className={classes.header}>
        <div className={classes.headerTitle}>
          <Typography variant="subtitle2">Validation</Typography>
          {errorCount > 0 && (
            <Chip 
              size="small" 
              label={errorCount}
              className={`${classes.severityChip} ${classes.errorChip}`}
            />
          )}
          {warningCount > 0 && (
            <Chip 
              size="small" 
              label={warningCount}
              className={`${classes.severityChip} ${classes.warningChip}`}
            />
          )}
        </div>
        <IconButton size="small" onClick={onClose}>
          <CloseIcon fontSize="small" />
        </IconButton>
      </div>

      {/* Content */}
      <div className={classes.content}>
        {errors.length === 0 ? (
          <div className={classes.noErrors}>
            <CheckCircleIcon className={classes.noErrorsIcon} />
            <Typography variant="subtitle2">All Good!</Typography>
            <Typography variant="caption" color="textSecondary">
              Your YAML is valid.
            </Typography>
          </div>
        ) : (
          <>
            {/* Errors List */}
            <div className={classes.errorSection}>
              <Typography className={classes.sectionTitle}>Issues</Typography>
              <List className={classes.errorsList} dense disablePadding>
                {errors.map((error) => (
                  <ListItem
                    key={error.id}
                    className={`${classes.errorsListItem} ${
                      selectedError?.id === error.id ? classes.selectedError : ''
                    }`}
                    onClick={() => {
                      // Toggle: if already selected, deselect; otherwise select
                      onSelectError(selectedError?.id === error.id ? null : error);
                    }}
                    button
                  >
                    <ListItemIcon style={{ minWidth: 28 }}>
                      {getSeverityIcon(error.severity)}
                    </ListItemIcon>
                    <ListItemText
                      primary={error.message}
                      secondary={error.path && `${error.path}`}
                      primaryTypographyProps={{ variant: 'caption', noWrap: true }}
                      secondaryTypographyProps={{ variant: 'caption', noWrap: true, style: { fontSize: '0.7rem' } }}
                    />
                  </ListItem>
                ))}
              </List>
            </div>

            <Divider />

            {/* Selected Error Details */}
            {selectedError ? (
              <Box mt={1.5}>
                {/* Error Message */}
                <div className={classes.errorSection}>
                  <div style={{ display: 'flex', alignItems: 'flex-start', gap: 6 }}>
                    {getSeverityIcon(selectedError.severity)}
                    <div style={{ flex: 1 }}>
                      <Typography className={classes.errorMessage}>
                        {selectedError.message}
                      </Typography>
                      {selectedError.code && (
                        <Chip 
                          size="small" 
                          label={selectedError.code}
                          variant="outlined"
                          style={{ height: 18, fontSize: '0.65rem' }}
                        />
                      )}
                    </div>
                  </div>
                </div>

                {/* Location */}
                {(selectedError.line || selectedError.path) && (
                  <div className={classes.errorSection}>
                    <Typography className={classes.sectionTitle}>Location</Typography>
                    {selectedError.path && (
                      <Chip 
                        icon={<CodeIcon style={{ fontSize: 14 }} />}
                        label={selectedError.path}
                        size="small"
                        className={classes.pathChip}
                        variant="outlined"
                      />
                    )}
                    {selectedError.line && (
                      <div className={classes.locationInfo}>
                        Line {selectedError.line}
                        {selectedError.column && `, Col ${selectedError.column}`}
                        {onGoToLine && (
                          <Button 
                            size="small" 
                            onClick={handleGoToError}
                            style={{ marginLeft: 4, padding: '0 4px', minWidth: 'auto', fontSize: '0.7rem' }}
                          >
                            Go to
                          </Button>
                        )}
                      </div>
                    )}
                  </div>
                )}

                {/* Details */}
                {selectedError.details && (
                  <div className={classes.errorSection}>
                    <Typography className={classes.sectionTitle}>Details</Typography>
                    <Typography className={classes.errorDetails}>
                      {selectedError.details}
                    </Typography>
                  </div>
                )}

                {/* Current Value */}
                {selectedError.currentValue && (
                  <div className={classes.errorSection}>
                    <Typography className={classes.sectionTitle}>Current Value</Typography>
                    <Typography className={classes.currentValueLabel}>Invalid:</Typography>
                    <div className={classes.codeBlock}>
                      {selectedError.currentValue}
                    </div>
                  </div>
                )}

                {/* Suggestions */}
                {selectedError.suggestions && selectedError.suggestions.length > 0 && (
                  <div className={classes.errorSection}>
                    <div
                      role="button"
                      tabIndex={0}
                      aria-expanded={suggestionsExpanded}
                      style={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}
                      onClick={() => setSuggestionsExpanded(!suggestionsExpanded)}
                      onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') setSuggestionsExpanded(!suggestionsExpanded); }}
                    >
                      <Typography className={classes.sectionTitle} style={{ flex: 1, marginBottom: 0 }}>
                        Suggestions ({selectedError.suggestions.length})
                      </Typography>
                      <IconButton size="small" className={classes.expandButton}>
                        {suggestionsExpanded ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
                      </IconButton>
                    </div>
                    <Collapse in={suggestionsExpanded}>
                      <div className={classes.suggestionsList} style={{ marginTop: 4 }}>
                        {selectedError.suggestions.map((suggestion: string, idx: number) => (
                          <div key={idx} className={classes.suggestionItem}>
                            <Typography className={classes.suggestionText}>
                              {suggestion}
                            </Typography>
                            <Tooltip title="Copy">
                              <IconButton
                                size="small"
                                className={classes.copyButton}
                                onClick={() => handleCopySuggestion(suggestion)}
                              >
                                <FileCopyIcon style={{ fontSize: 14 }} />
                              </IconButton>
                            </Tooltip>
                          </div>
                        ))}
                      </div>
                    </Collapse>
                  </div>
                )}

                {/* Schema Info for Field */}
                {selectedError.path && entityKind && (
                  <div className={classes.errorSection}>
                    <div
                      role="button"
                      tabIndex={0}
                      aria-expanded={schemaInfoExpanded}
                      style={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}
                      onClick={() => setSchemaInfoExpanded(!schemaInfoExpanded)}
                      onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') setSchemaInfoExpanded(!schemaInfoExpanded); }}
                    >
                      <HelpOutlineIcon style={{ fontSize: 14, marginRight: 4 }} color="primary" />
                      <Typography className={classes.sectionTitle} style={{ flex: 1, marginBottom: 0 }}>
                        Schema Reference
                      </Typography>
                      <IconButton size="small" className={classes.expandButton}>
                        {schemaInfoExpanded ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
                      </IconButton>
                    </div>
                    <Collapse in={schemaInfoExpanded}>
                      {loadingFieldInfo && (
                        <Box display="flex" alignItems="center" style={{ gap: 8 }} mt={1}>
                          <CircularProgress size={16} />
                          <Typography variant="caption" color="textSecondary">
                            Loading schema info...
                          </Typography>
                        </Box>
                      )}
                      {!loadingFieldInfo && fieldInfo && (
                        <Box mt={1}>
                          <Typography variant="caption" color="textSecondary" style={{ display: 'block', marginBottom: 4 }}>
                            <strong>Description:</strong>
                          </Typography>
                          <Typography variant="body2" style={{ fontSize: '0.8rem', marginBottom: 8 }}>
                            {fieldInfo.description}
                          </Typography>

                          {/* Type & Required */}
                          <Box display="flex" style={{ gap: 4 }} mb={1}>
                            <Chip
                              size="small"
                              label={fieldInfo.type}
                              variant="outlined"
                              style={{ height: 20, fontSize: '0.65rem', fontFamily: 'monospace' }}
                            />
                            <Chip
                              size="small"
                              label={fieldInfo.required ? 'required' : 'optional'}
                              style={{
                                height: 20,
                                fontSize: '0.65rem',
                                backgroundColor: fieldInfo.required ? '#f44336' : '#9e9e9e',
                                color: '#fff',
                              }}
                            />
                          </Box>

                          {/* Valid Values */}
                          {fieldInfo.validValues && fieldInfo.validValues.length > 0 && (
                            <Box mb={1}>
                              <Typography variant="caption" color="textSecondary" style={{ display: 'block', marginBottom: 4 }}>
                                <strong>Valid values:</strong>
                              </Typography>
                              <Box display="flex" flexWrap="wrap" style={{ gap: 4 }}>
                                {fieldInfo.validValues.map((value, i) => (
                                  <Tooltip key={i} title="Click to copy">
                                    <Chip
                                      size="small"
                                      label={value}
                                      clickable
                                      onClick={() => handleCopySuggestion(value)}
                                      style={{
                                        height: 22,
                                        fontSize: '0.7rem',
                                        fontFamily: 'monospace',
                                        cursor: 'pointer',
                                      }}
                                    />
                                  </Tooltip>
                                ))}
                              </Box>
                            </Box>
                          )}

                          {/* Examples */}
                          {fieldInfo.examples && fieldInfo.examples.length > 0 && (
                            <Box mb={1}>
                              <Typography variant="caption" color="textSecondary" style={{ display: 'block', marginBottom: 4 }}>
                                <strong>Examples:</strong>
                              </Typography>
                              <div style={{ fontFamily: 'monospace', fontSize: '0.75rem' }}>
                                {fieldInfo.examples.map((example, i) => (
                                  <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 4, marginBottom: 2 }}>
                                    <span style={{ color: '#666' }}>•</span>
                                    <span>{example}</span>
                                    <Tooltip title="Copy">
                                      <IconButton
                                        size="small"
                                        style={{ padding: 2 }}
                                        onClick={() => handleCopySuggestion(example)}
                                      >
                                        <FileCopyIcon style={{ fontSize: 12 }} />
                                      </IconButton>
                                    </Tooltip>
                                  </div>
                                ))}
                              </div>
                            </Box>
                          )}

                          {/* Docs Link */}
                          {fieldInfo.docsUrl && (
                            <Link
                              href={fieldInfo.docsUrl}
                              target="_blank"
                              rel="noopener noreferrer"
                              style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: '0.75rem', marginTop: 4 }}
                            >
                              <LinkIcon style={{ fontSize: 14 }} />
                              View Field Documentation
                            </Link>
                          )}
                        </Box>
                      )}
                      {!loadingFieldInfo && !fieldInfo && (
                        <Typography variant="caption" color="textSecondary" style={{ display: 'block', marginTop: 4 }}>
                          No schema info available for this field
                        </Typography>
                      )}
                    </Collapse>
                  </div>
                )}

                {/* Documentation Link */}
                {selectedError.docsUrl && (
                  <Link 
                    href={selectedError.docsUrl} 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className={classes.docsLink}
                  >
                    <LinkIcon fontSize="small" />
                    View Documentation
                  </Link>
                )}
              </Box>
            ) : (
              <Box mt={2} textAlign="center" color="text.secondary">
                <Typography variant="caption">
                  Select an issue to see details
                </Typography>
              </Box>
            )}
          </>
        )}
      </div>
      </Paper>
    </div>
  );
};

export default ErrorDetailsSidebar;
